import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

function isShowing(action) {
  if (action.show === undefined) return true;
  return action.show === true;
}

/**
 * Mobile FAB for project create-actions. Portaled so sticky headers / overflow
 * cannot clip the action stack. Sits above the bottom nav.
 */
export function ProjectFab({
  actions = [],
  onRunAction,
  openPickerRef,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const visible = actions.filter(
    (action) => action.active && isShowing(action)
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (visible.length === 0 || typeof document === "undefined") return null;

  const run = (action) => {
    setOpen(false);
    if (action.directUpload) {
      openPickerRef?.current?.();
      return;
    }
    onRunAction?.(action);
  };

  return createPortal(
    <div className="lg:hidden">
      {open ? (
        <button
          type="button"
          className="fixed inset-0 z-[58] bg-black/25"
          aria-label={t("project.fab.close")}
          onClick={() => setOpen(false)}
        />
      ) : null}

      <div
        className="pointer-events-none fixed right-5 z-[59] flex flex-col items-end gap-2.5"
        style={{
          bottom: "calc(4.75rem + env(safe-area-inset-bottom, 0px))",
        }}
      >
        {open
          ? visible.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.label}
                  type="button"
                  onClick={() => run(action)}
                  className="pointer-events-auto inline-flex h-10 items-center gap-2.5 rounded-full border border-[#dfe5ec] bg-white px-3.5 text-[13.5px] font-semibold text-[#33445a] shadow-[0_6px_18px_rgba(20,35,55,.12)]"
                >
                  {Icon ? <Icon className="h-4 w-4 text-[#5b6b7f]" aria-hidden /> : null}
                  {action.mobileLabel || action.label}
                </button>
              );
            })
          : null}

        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="pointer-events-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#1463C7] text-white shadow-[0_10px_26px_rgba(20,99,199,.34)]"
          aria-expanded={open}
          aria-label={open ? t("project.fab.close") : t("project.fab.open")}
        >
          {open ? (
            <XMarkIcon className="h-7 w-7" aria-hidden />
          ) : (
            <PlusIcon className="h-7 w-7" aria-hidden />
          )}
        </button>
      </div>
    </div>,
    document.body
  );
}

export default ProjectFab;
